import { useEffect, useState, useCallback } from "react";
import { format } from "date-fns";
import { Warning, ArrowCounterClockwise, Trash } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { zar } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const varFlag = (l) => {
  const pct = Math.abs(l.variance_quantity) / Math.max(Math.abs(l.system_expected_quantity), 1e-9);
  if (pct > 0.05) return { label: "Investigate", cls: "bg-[#F87171]/10 text-[#F87171]" };
  if (pct > 0.03) return { label: "Needs attention", cls: "bg-[#D99A29]/10 text-[#D99A29]" };
  return null;
};

function VarianceTable({ lines, testid }) {
  const sorted = [...lines].sort((a, b) => Math.abs(b.variance_value) - Math.abs(a.variance_value));
  return (
    <div className="glass border border-border rounded-lg overflow-x-auto">
      <table className="w-full text-sm min-w-[640px]" data-testid={testid}>
        <thead><tr className="border-b text-left text-muted-foreground">
          <th className="px-4 py-3 font-medium">Item</th><th className="px-4 py-3 font-medium text-right">Expected</th>
          <th className="px-4 py-3 font-medium text-right">Counted</th><th className="px-4 py-3 font-medium text-right">Variance</th>
          <th className="px-4 py-3 font-medium text-right">Variance value</th><th className="px-4 py-3 font-medium">Flag</th>
        </tr></thead>
        <tbody>
          {sorted.map((l) => {
            const f = varFlag(l);
            return (
              <tr key={l.id} className="border-b last:border-0" data-testid={`variance-row-${l.stock_item_id}`}>
                <td className="px-4 py-2.5 font-medium">{l.stock_item_name} <span className="text-xs text-muted-foreground">({l.base_unit})</span></td>
                <td className="px-4 py-2.5 num text-right">{l.system_expected_quantity}</td>
                <td className="px-4 py-2.5 num text-right">{l.counted_quantity}</td>
                <td className={`px-4 py-2.5 num text-right ${l.variance_quantity !== 0 ? "text-[#F87171]" : ""}`}>{l.variance_quantity > 0 ? "+" : ""}{l.variance_quantity}</td>
                <td className="px-4 py-2.5 num text-right">{zar(l.variance_value)}</td>
                <td className="px-4 py-2.5">{f && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${f.cls}`}><Warning size={11} className="inline mr-1" />{f.label}</span>}</td>
              </tr>
            );
          })}
          {sorted.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No lines.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function StockTake() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [day, setDay] = useState(format(new Date(), "yyyy-MM-dd"));
  const [countedBy, setCountedBy] = useState("");
  const [counts, setCounts] = useState({});
  const [result, setResult] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/stock-items").then((r) => setItems(r.data)).catch(() => {});
    api.get("/stocktakes").then((r) => setHistory(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user) setCountedBy(`${user.first_name} ${user.last_name}`); }, [user]);

  const submit = async () => {
    const lines = items.filter((i) => counts[i.id] !== undefined && counts[i.id] !== "")
      .map((i) => ({ stock_item_id: i.id, counted_quantity: Number(counts[i.id]) }));
    if (!lines.length) return toast.error("Enter at least one counted quantity");
    setBusy(true);
    try {
      const { data } = await api.post("/stocktakes", { date: day, counted_by: countedBy, lines });
      setResult(data);
      setCounts({});
      load();
      toast.success("Stock take saved — ledger adjusted to physical counts");
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const remove = async (st) => {
    try {
      await api.delete(`/stocktakes/${st.id}`);
      toast.success("Stock take deleted — adjustments reversed");
      setDetail(null); load();
    } catch (e) { toast.error(apiError(e, "Delete failed")); }
  };

  return (
    <div className="space-y-6" data-testid="stocktake-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Stock Take</h1>
        <p className="text-muted-foreground mt-1">Count physical stock — the ledger snaps to your count and variance is reported</p>
      </div>

      <div className="glass border border-border rounded-lg p-4 space-y-4" data-testid="stocktake-new">
        <div className="flex flex-wrap gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Count date</Label><Input type="date" data-testid="stocktake-date" max={format(new Date(), "yyyy-MM-dd")} value={day} onChange={(e) => setDay(e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Counted by</Label><Input data-testid="stocktake-counted-by" value={countedBy} onChange={(e) => setCountedBy(e.target.value)} /></div>
        </div>
        <table className="w-full text-sm" data-testid="stocktake-items-table">
          <thead><tr className="border-b text-left text-muted-foreground">
            <th className="py-2 font-medium">Item</th><th className="py-2 font-medium text-right">System balance</th><th className="py-2 font-medium text-right w-36">Counted qty</th>
          </tr></thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-b last:border-0" data-testid={`stocktake-item-${i.id}`}>
                <td className="py-2 font-medium">{i.name} <span className="text-xs text-muted-foreground">({i.base_unit})</span></td>
                <td className="py-2 num text-right text-muted-foreground">{i.qty_on_hand ?? 0}</td>
                <td className="py-2"><Input type="number" step="any" min="0" className="w-28 ml-auto text-right" data-testid={`stocktake-count-${i.id}`} value={counts[i.id] ?? ""} onChange={(e) => setCounts({ ...counts, [i.id]: e.target.value })} /></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-muted-foreground">No stock items yet.</td></tr>}
          </tbody>
        </table>
        <Button onClick={submit} disabled={busy || items.length === 0} data-testid="stocktake-submit-btn" className="bg-primary hover:bg-[#8B5CF6] text-white">
          {busy ? "Saving…" : "Save stock take"}
        </Button>
      </div>

      {result && (
        <div className="space-y-3" data-testid="stocktake-result">
          <p className="font-heading font-semibold text-lg">Variance report — {result.date}</p>
          <VarianceTable lines={result.lines} testid="stocktake-variance-table" />
        </div>
      )}

      <div className="space-y-3">
        <p className="font-heading font-semibold text-lg">Past stock takes</p>
        <div className="glass border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="stocktake-history-table">
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-b last:border-0 hover:bg-accent/40 cursor-pointer" data-testid={`stocktake-history-${h.id}`} onClick={async () => { const { data } = await api.get(`/stocktakes/${h.id}`); setDetail(data); }}>
                  <td className="px-4 py-2.5 font-medium">{h.date}</td>
                  <td className="px-4 py-2.5">{h.counted_by}</td>
                  <td className="px-4 py-2.5 text-right"><ArrowCounterClockwise size={14} className="inline text-muted-foreground" /></td>
                </tr>
              ))}
              {history.length === 0 && <tr><td className="px-4 py-8 text-center text-muted-foreground">No stock takes yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="stocktake-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading">Stock take {detail?.date} — {detail?.counted_by}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Stock take line detail</DialogDescription>
          {detail && <VarianceTable lines={detail.lines} testid="stocktake-detail-table" />}
          {detail && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-destructive mt-3" data-testid="stocktake-delete-btn"><Trash size={15} className="mr-1" /> Delete (reverses adjustments)</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this stock take?</AlertDialogTitle>
                  <AlertDialogDescription>The stocktake_adjustment movements it posted will be reversed.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => remove(detail)} data-testid="stocktake-delete-confirm" className="bg-destructive text-white hover:bg-destructive/90">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
